import { AdjustColorTint, colors } from 'utils/colors'

export const lightRed = AdjustColorTint('#E24C4C', 20)
export const lightGreen = AdjustColorTint('#4CE2A7', 20)
export const lightYellow = AdjustColorTint('#F1A153', 20)
export const lighterYellow = AdjustColorTint('#F1A153', 70)
export const darkRed = AdjustColorTint('#E24C4C', -20)
export const darkGreen = AdjustColorTint('#4CE2A7', -20)
export const darkYellow = AdjustColorTint('#F1A153', -20)

/**
 * Base styling for all variants
 */
export const base = {
  '&:disabled': {
    color: colors.disabledText,
    '& > svg': {
      color: colors.disabledText,
    },
    '&:hover': {
      color: colors.disabledText,
    },
  },
  border: '1px solid',
}

/**
 * Fill button style
 */
export const fill = {
  bg: 'blue1',
  borderColor: 'blue1',
  color: 'white',

  // Hover style
  '&:hover': {
    bg: 'blue2',
    borderColor: 'blue2',
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: 'blue3',
    borderColor: 'blue3',
    boxShadow: '0 0 0 4px rgba(143, 155, 179, 0.1)',
    '&:disabled': {
      boxShadow: 'none',
    },
  },

  // Disabled style
  '&:disabled': {
    bg: 'gray3',
    borderColor: 'gray3',

    '&:hover': {
      bg: 'gray3',
    },
  },
}

/**
 * Outline button style
 */
const disabledOutline = {
  bg: 'gray3',
  borderColor: 'gray4',

  '&:hover': {
    bg: 'gray3',
  },
}

export const outline = {
  bg: 'white',
  borderColor: 'defaultBorder',
  color: 'blue1',

  '& > svg': {
    color: 'blue1',
  },

  // Hover style
  '&:hover': {
    bg: 'blue5',
    borderColor: 'blue1',
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: 'blue5',
    borderColor: 'blue3',
    boxShadow: '0 0 0 4px rgba(143, 155, 179, 0.1)',
    '&:disabled': {
      boxShadow: 'none',
    },
  },

  // Disabled style
  '&:disabled': disabledOutline,
}

export const blandOutline = {
  bg: 'white',
  borderColor: 'defaultBorder',
  color: 'gray5',

  '& > svg': {
    color: 'gray5',
  },

  // Hover style
  '&:hover': {
    bg: 'rgba(89, 139, 255, 0.1)',
    borderColor: 'gray6',
    color: 'gray6',

    '& > svg': {
      color: 'gray6',
    },
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: 'rgba(197, 206, 224, 0.5)',
    borderColor: 'gray3',
    color: 'gray5',
    boxShadow: '0 0 0 4px rgba(143, 155, 179, 0.1)',
    '&:disabled': {
      boxShadow: 'none',
    },
  },

  // Disabled style
  '&:disabled': disabledOutline,
}

/**
 * Ghost button style
 */
const disabledGhost = {
  bg: 'rgba(143, 155, 179, 0.1)',

  '&:hover': {
    bg: 'rgba(143, 155, 179, 0.1)',
  },
}

export const ghost = {
  bg: 'transparent',
  borderColor: 'transparent',
  color: 'blue1',

  '& > svg': {
    color: 'blue1',
  },

  // Hover style
  '&:hover': {
    bg: 'blue5',
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: '#d6def7',
  },

  // Disabled style
  '&:disabled': disabledGhost,
}

export const blandGhost = {
  bg: 'transparent',
  borderColor: 'transparent',
  color: 'gray5',

  '& > svg': {
    color: 'gray5',
  },

  // Hover style
  '&:hover': {
    bg: 'blue5',
    color: 'blue1',

    '& > svg': {
      color: 'blue1',
    },
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: '#d6def7',
    borderColor: 'gray3',
    color: 'gray5',
  },

  // Disabled style
  '&:disabled': disabledGhost,
}

export const criticalGhost = {
  bg: 'transparent',
  borderColor: 'transparent',
  color: 'gray5',

  '& > svg': {
    color: 'gray5',
  },

  // Hover style
  '&:hover': {
    bg: 'gray1',
    color: 'red1',
    borderColor: 'red1',

    '& > svg': {
      color: 'red1',
    },
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: '#d6def7',
    borderColor: 'gray3',
    color: 'red',
  },

  // Disabled style
  '&:disabled': disabledGhost,
}

/**
 * Error button style
 */
const disabledCritical = {
  bg: 'rgba(143, 155, 179, 0.1)',
  borderColor: 'rgba(143, 155, 179, 0.1)',

  '&:hover': {
    bg: 'rgba(143, 155, 179, 0.1)',
  },
}

export const error = {
  bg: 'red',
  borderColor: 'red',
  color: 'white',

  // Hover style
  '&:hover': {
    bg: lightRed,
    borderColor: lightRed,
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: darkRed,
    borderColor: darkRed,
  },

  // Disabled style
  '&:disabled': disabledCritical,
}

/**
 * Critical outline button style
 */
export const criticalOutline = {
  bg: 'white',
  borderColor: 'defaultBorder',
  color: 'red',

  '& > svg': {
    color: 'red',
  },

  // Hover style
  '&:hover': {
    borderColor: lightRed,
    color: lightRed,
  },

  // Active Focus style
  '&:active, &[data-active]': {
    borderColor: darkRed,
    color: darkRed,
    boxShadow: '0 0 0 4px rgba(143, 155, 179, 0.1)',
    '&:disabled': {
      boxShadow: 'none',
    },
  },

  // Disabled style
  '&:disabled': disabledCritical,
}

/**
 * Success outline button style
 */
export const successOutline = {
  bg: 'transparent',
  borderColor: 'green',
  color: 'green',

  '& > svg': {
    color: 'green',
  },

  // Hover style
  '&:hover': {
    borderColor: lightGreen,
    color: lightGreen,
  },

  // Active Focus style
  '&:active, &[data-active]': {
    borderColor: darkGreen,
    color: darkRed,
    boxShadow: '0 0 0 4px rgba(143, 155, 179, 0.1)',
    '&:disabled': {
      boxShadow: 'none',
    },
  },

  // Disabled style
  '&:disabled': disabledOutline,
}

/**
 * Success button style
 */
export const success = {
  bg: 'green',
  borderColor: 'green',
  color: 'white',

  // Hover style
  '&:hover': {
    bg: lightGreen,
    borderColor: lightGreen,
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: darkGreen,
    borderColor: darkGreen,
  },

  // Disabled style
  '&:disabled': {
    bg: 'rgba(143, 155, 179, 0.1)',
    borderColor: 'rgba(143, 155, 179, 0.1)',

    '&:hover': {
      bg: 'rgba(143, 155, 179, 0.1)',
    },
  },
}

/**
 * Warning button style
 */
export const warning = {
  bg: 'yellow',
  borderColor: 'yellow',
  color: 'white',

  // Hover style
  '&:hover': {
    bg: lightYellow,
    borderColor: lightYellow,
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: darkYellow,
    borderColor: darkYellow,
  },

  // Disabled style
  '&:disabled': {
    bg: 'rgba(143, 155, 179, 0.1)',
    borderColor: 'rgba(143, 155, 179, 0.1)',

    '&:hover': {
      bg: 'rgba(143, 155, 179, 0.1);',
    },
  },
}

export const dashed = {
  bg: 'transparent',
  border: '1px dashed',
  borderColor: 'gray4',
  color: 'gray4',

  // Hover style
  '&:hover': {
    bg: 'gray1',
    color: 'blue2',

    '& > svg': {
      color: 'blue2',
    },
  },

  // Active Focus style
  '&:active, &[data-active]': {
    bg: 'rgba(143, 155, 179, 0.1)',
    borderColor: 'gray3',
    color: 'blue1',
  },

  // Disabled style
  '&:disabled': {
    bg: 'rgba(143, 155, 179, 0.1)',

    '&:hover': {
      bg: 'rgba(143, 155, 179, 0.1)',
    },
  },
}
